import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Target, RefreshCw, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Loader2, Shield, Activity, Calendar,
  DollarSign, BarChart3, Eye, Brain, Skull, Crown,
} from "lucide-react";
import DailyTradeCard from "@/components/DailyTradeCard";
import AuraLightweightChart from "@/components/AuraLightweightChart";
import { MarkdownText } from "@/components/MarkdownText";
import { tradingWeekLabel } from "@/lib/tradingWeek";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ====== Conviction tier styling ======
const TIER_STYLES = {
  HIGH: "from-green-500/20 to-emerald-500/10 border-green-500/40",
  MEDIUM: "from-amber-500/20 to-yellow-500/10 border-amber-500/40",
  LOW: "from-orange-500/20 to-red-500/10 border-orange-500/40",
  NO_TRADE: "from-slate-500/20 to-slate-500/5 border-slate-500/40",
};

const BIAS_STYLES = {
  LONG: { color: "text-green-400", bg: "bg-green-500/15", icon: TrendingUp, label: "LONG" },
  SHORT: { color: "text-red-400", bg: "bg-red-500/15", icon: TrendingDown, label: "SHORT" },
  NEUTRAL: { color: "text-slate-400", bg: "bg-slate-500/15", icon: Minus, label: "NEUTRAL" },
};

const formatNum = (n, dec = 0) => n?.toLocaleString("en-US", { maximumFractionDigits: dec });
const formatPrice = (n) => `$${n?.toFixed(2)}`;
const formatPct = (n) => `${n > 0 ? "+" : ""}${n?.toFixed(2)}%`;
const formatBps = (n) => `${n > 0 ? "+" : ""}${n?.toFixed(0)}bps`;

// ====== Sub-components ======

const ConvictionGauge = ({ score, tier }) => {
  const tierColor = {
    HIGH: "text-green-400",
    MEDIUM: "text-amber-400",
    LOW: "text-orange-400",
    NO_TRADE: "text-slate-400",
  }[tier] || "text-slate-400";

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative w-24 h-24 flex items-center justify-center">
        <svg className="w-24 h-24 -rotate-90 absolute inset-0">
          <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="none" className="text-border" />
          <circle
            cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="none"
            strokeDasharray={`${2 * Math.PI * 40}`}
            strokeDashoffset={`${2 * Math.PI * 40 * (1 - score / 100)}`}
            className={tierColor}
            strokeLinecap="round"
          />
        </svg>
        <div className="text-center z-10">
          <div className={`text-2xl font-bold ${tierColor}`}>{score}</div>
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">conviction</div>
        </div>
      </div>
      <Badge className={`mt-1 text-[10px] ${tierColor} bg-transparent border`}>{tier}</Badge>
    </div>
  );
};

const FactorBar = ({ label, score, weight }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-32 text-muted-foreground capitalize truncate">{label.replace(/_/g, " ")}</span>
    <div className="flex-1 h-2 bg-border/50 rounded-full overflow-hidden">
      <div
        className={`h-full ${score >= 7 ? "bg-green-500" : score >= 5 ? "bg-amber-500" : "bg-red-500"}`}
        style={{ width: `${score * 10}%` }}
      />
    </div>
    <span className="w-12 text-right font-mono">{score.toFixed(1)}/10</span>
    <span className="w-10 text-right text-[10px] text-muted-foreground">{(weight * 100).toFixed(0)}%</span>
  </div>
);

const FACTOR_WEIGHTS = {
  cot_extremity: 0.20,
  cot_momentum: 0.10,
  real_yield: 0.20,
  dxy: 0.10,
  technical_confluence: 0.15,
  risk_reward: 0.10,
  event_risk: 0.10,
  seasonality: 0.05,
};

export default function AuraXAUUSD() {
  const [thesis, setThesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("brief");
  const [account, setAccount] = useState(null);

  const fetchThesis = async () => {
    try {
      const res = await axios.get(`${API}/aura/xauusd/thesis`);
      if (res.data.available) setThesis(res.data.thesis);
    } catch (e) {
      console.error("Fetch thesis", e);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/aura/xauusd/history?weeks=12`);
      setHistory(res.data.theses || []);
    } catch (e) { console.error(e); }
  };

  const fetchAccount = async () => {
    try {
      const res = await axios.get(`${API}/aura/xauusd/account`);
      setAccount(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    Promise.all([fetchThesis(), fetchHistory(), fetchAccount()]).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/aura/xauusd/refresh`, {}, { timeout: 240000 });
      if (res.data.success) {
        setThesis(res.data.thesis);
        await fetchHistory();
      }
    } catch (e) {
      console.error("Refresh failed", e);
      alert("Refresh failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="aura-loading">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!thesis) {
    return (
      <div className="space-y-6" data-testid="aura-no-thesis">
        <Header onRefresh={refresh} refreshing={refreshing} />
        <Card className="border-amber-500/30">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 text-amber-500/50 mx-auto mb-3" />
            <h3 className="text-lg font-semibold mb-2">No thesis generated yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Click "Generate Thesis" to run the COT-driven multi-agent analysis. Takes ~60-120 seconds.
            </p>
            <Button onClick={refresh} disabled={refreshing} data-testid="aura-generate-btn">
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
              {refreshing ? "Analysing..." : "Generate Thesis"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const bias = BIAS_STYLES[thesis.bias] || BIAS_STYLES.NEUTRAL;
  const BiasIcon = bias.icon;
  const tierStyle = TIER_STYLES[thesis.conviction_tier] || TIER_STYLES.NO_TRADE;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="aura-xauusd-page">
      <Header onRefresh={refresh} refreshing={refreshing} />

      {/* Top Banner */}
      <Card className={`bg-gradient-to-br ${tierStyle} border-2`} data-testid="aura-thesis-banner">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${bias.bg}`}>
                <BiasIcon className={`w-8 h-8 ${bias.color}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`${bias.bg} ${bias.color} border-0 text-sm font-bold`}>
                    {bias.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground" data-testid="aura-week-label">{tradingWeekLabel(thesis.generated_at)}</span>
                </div>
                <p className="text-sm font-medium text-foreground" data-testid="aura-headline">
                  {thesis.orchestrator_synthesis?.split(".")[0] || "XAUUSD Weekly Thesis"}
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span>Spot: <span className="text-foreground font-mono font-semibold">{formatPrice(thesis.current_price)}</span></span>
                  <span>•</span>
                  <span>Generated: {new Date(thesis.generated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <ConvictionGauge score={thesis.conviction} tier={thesis.conviction_tier} />
          </div>
        </CardContent>
      </Card>

      <DailyTradeCard symbol="XAUUSD=X" decimals={2} />

      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="aura-tabs">
        <TabsList className="grid grid-cols-6 w-full max-w-2xl">
          <TabsTrigger value="brief" data-testid="tab-brief">Brief</TabsTrigger>
          <TabsTrigger value="chart" data-testid="tab-chart">Chart</TabsTrigger>
          <TabsTrigger value="cot" data-testid="tab-cot">COT</TabsTrigger>
          <TabsTrigger value="macro" data-testid="tab-macro">Macro</TabsTrigger>
          <TabsTrigger value="agents" data-testid="tab-agents">Debate</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        {/* CHART TAB */}
        <TabsContent value="chart" className="mt-4">
          <AuraLightweightChart asset="XAUUSD" decimals={2} label="XAU/USD" />
        </TabsContent>

        {/* BRIEF TAB */}
        <TabsContent value="brief" className="space-y-4 mt-4">
          <EntryPlanCard thesis={thesis} bias={bias} />
          <SizingCard sizing={thesis.sizing} account={account} />
          <FactorBreakdownCard factor_scores={thesis.factor_scores} />
          <InvalidationCard triggers={thesis.invalidation_triggers} entry_plan={thesis.entry_plan} />
        </TabsContent>

        {/* COT TAB */}
        <TabsContent value="cot" className="space-y-4 mt-4">
          <COTCard cot={thesis.cot} />
        </TabsContent>

        {/* MACRO TAB */}
        <TabsContent value="macro" className="space-y-4 mt-4">
          <MacroCard macro={thesis.macro} />
        </TabsContent>

        {/* AGENTS TAB */}
        <TabsContent value="agents" className="space-y-4 mt-4">
          <AgentCard title="Scout — Bull/Bear Case" icon={Eye} color="text-green-400" text={thesis.scout_analysis} />
          <AgentCard title="Skeptic — Risk Officer" icon={Skull} color="text-red-400" text={thesis.skeptic_analysis} />
          <AgentCard title="Orchestrator — Final Call" icon={Crown} color="text-amber-400" text={thesis.orchestrator_synthesis} />
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="space-y-3 mt-4">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No past theses yet.</p>
          ) : (
            history.map((h) => (
              <Card key={h.id} data-testid={`history-${h.week_key}`} className="hover:bg-card/60 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Badge className={`${BIAS_STYLES[h.bias]?.bg} ${BIAS_STYLES[h.bias]?.color} border-0`}>
                        {h.bias}
                      </Badge>
                      <span className="text-sm font-mono">{h.week_key}</span>
                      <span className="text-xs text-muted-foreground">@ {formatPrice(h.current_price)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">{h.conviction}/100 {h.conviction_tier}</span>
                      {h.entry_plan?.direction !== "NO_TRADE" && (
                        <span className="text-muted-foreground font-mono">
                          {formatPrice(h.entry_plan?.primary_entry)} → SL {formatPrice(h.entry_plan?.stop_loss)}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ====== Components ======

const Header = ({ onRefresh, refreshing }) => (
  <div className="flex items-center justify-between flex-wrap gap-3" data-testid="aura-header">
    <div className="flex items-center gap-3">
      <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30">
        <Target className="w-6 h-6 text-amber-400" />
      </div>
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          AuraTrade — XAUUSD Weekly Thesis
        </h1>
        <p className="text-xs text-muted-foreground">
          COT-driven multi-agent analysis · Saturday auto-refresh · $3,000 VT Markets account
        </p>
      </div>
    </div>
    <Button onClick={onRefresh} disabled={refreshing} variant="outline" data-testid="aura-refresh-btn">
      {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
      {refreshing ? "Analysing..." : "Refresh Thesis"}
    </Button>
  </div>
);

const EntryPlanCard = ({ thesis, bias }) => {
  const ep = thesis.entry_plan;
  if (ep.direction === "NO_TRADE") {
    return (
      <Card className="border-slate-500/40" data-testid="entry-plan-no-trade">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Minus className="w-5 h-5 text-slate-400" /> No Trade This Week
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{ep.invalidation_note}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-amber-500/30" data-testid="entry-plan-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className={`w-5 h-5 ${bias.color}`} /> Entry Plan
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <KV label="Entry 1" value={formatPrice(ep.primary_entry)} testid="entry-1" />
          <KV label="Entry 2" value={ep.secondary_entry ? formatPrice(ep.secondary_entry) : "—"} testid="entry-2" />
          <KV label="Stop Loss" value={formatPrice(ep.stop_loss)} className="text-red-400" testid="stop-loss" />
          <KV label="Zone" value={`${formatPrice(ep.entry_zone_low)} – ${formatPrice(ep.entry_zone_high)}`} testid="entry-zone" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm pt-3 border-t border-border/50">
          <KV label="TP1" value={formatPrice(ep.tp1)} className="text-green-400" testid="tp1" />
          <KV label="TP2" value={ep.tp2 ? formatPrice(ep.tp2) : "—"} className="text-green-400" testid="tp2" />
          <KV label="R:R to TP1" value={ep.rr_to_tp1?.toFixed(2)} testid="rr-tp1" />
          <KV label="R:R to TP2" value={ep.rr_to_tp2 ? ep.rr_to_tp2?.toFixed(2) : "—"} testid="rr-tp2" />
        </div>
        <div className="text-xs text-muted-foreground pt-2 border-t border-border/50" data-testid="invalidation-note">
          <span className="font-semibold">Invalidation:</span> {ep.invalidation_note}
        </div>
      </CardContent>
    </Card>
  );
};

const SizingCard = ({ sizing, account }) => {
  if (!sizing) return null;
  const blocked = sizing.lot_per_entry === 0;
  return (
    <Card className={blocked ? "border-red-500/40 bg-red-500/5" : ""} data-testid="sizing-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          Position Sizing
          {blocked && <Badge className="bg-red-500/20 text-red-400 border-red-500/40 text-xs">RISK GATE BLOCKED</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <KV label="Account" value={`$${formatNum(sizing.account_size)}`} testid="sizing-account" />
          <KV label="Risk %" value={`${sizing.risk_pct}%`} className={sizing.risk_pct > 3 ? "text-red-400" : ""} testid="sizing-risk-pct" />
          <KV label="Risk $" value={`$${sizing.risk_usd}`} testid="sizing-risk-usd" />
          <KV label="Stop Dist" value={`$${sizing.stop_distance_usd}`} testid="sizing-stop-dist" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm pt-3 border-t border-border/50">
          <KV label="Lot/Entry" value={sizing.lot_per_entry} testid="lot-per-entry" />
          <KV label="Total Lot" value={sizing.total_lot} testid="total-lot" />
          <KV label="Notional" value={`$${formatNum(sizing.notional_exposure)}`} testid="notional" />
          <KV label="Max Loss" value={`$${sizing.expected_loss_if_stopped}`} className="text-red-400" testid="max-loss" />
        </div>
        {sizing.entry_breakdown?.length > 0 && (
          <div className="space-y-1.5 pt-3 border-t border-border/50">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Order Breakdown</div>
            {sizing.entry_breakdown.map((b, i) => (
              <div key={i} className="text-xs font-mono bg-card/40 rounded p-2" data-testid={`breakdown-${i}`}>
                {b.type === "BLOCKED" ? (
                  <div className="text-red-400">
                    <AlertTriangle className="w-3 h-3 inline mr-1" /> {b.note}
                  </div>
                ) : (
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span>#{b.entry_no} · {formatPrice(b.price)} · {b.lot} lot · {b.type}</span>
                    {b.note && <span className="text-muted-foreground text-[10px]">{b.note}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!blocked && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border/50 text-sm">
            <KV label="TP1 win" value={`+$${sizing.expected_profit_tp1}`} className="text-green-400" testid="tp1-profit" />
            <KV label="TP2 win" value={`+$${sizing.expected_profit_tp2}`} className="text-green-400" testid="tp2-profit" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FactorBreakdownCard = ({ factor_scores }) => (
  <Card data-testid="factor-breakdown">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-blue-400" /> Conviction Factor Breakdown
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      {Object.entries(factor_scores || {}).map(([k, v]) => (
        <FactorBar key={k} label={k} score={v} weight={FACTOR_WEIGHTS[k] || 0} />
      ))}
    </CardContent>
  </Card>
);

const InvalidationCard = ({ triggers, entry_plan }) => (
  <Card className="border-red-500/30" data-testid="invalidation-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-red-400" /> What Kills This Trade
      </CardTitle>
    </CardHeader>
    <CardContent>
      {triggers?.length > 0 ? (
        <ul className="space-y-1.5 text-sm">
          {triggers.map((t, i) => (
            <li key={i} className="flex items-start gap-2" data-testid={`invalidator-${i}`}>
              <span className="text-red-400 mt-1">•</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No specific invalidators flagged.</p>
      )}
    </CardContent>
  </Card>
);

const COTCard = ({ cot }) => {
  if (!cot) return null;
  return (
    <Card data-testid="cot-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-400" /> COT Positioning · {cot.report_date}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-purple-500/5 border border-purple-500/20 rounded p-3 text-sm">
          {cot.interpretation}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <KV label="MM Net" value={formatNum(cot.mm_net)} className={cot.mm_net > 0 ? "text-green-400" : "text-red-400"} testid="cot-mm-net" />
          <KV label="MM Long" value={formatNum(cot.mm_long)} testid="cot-mm-long" />
          <KV label="MM Short" value={formatNum(cot.mm_short)} testid="cot-mm-short" />
          <KV label="Z-score 52w" value={`${cot.mm_z_52w > 0 ? "+" : ""}${cot.mm_z_52w}σ`} testid="cot-z-52" />
          <KV label="Z-score 156w" value={`${cot.mm_z_156w > 0 ? "+" : ""}${cot.mm_z_156w}σ`} testid="cot-z-156" />
          <KV label="MM Index (52w)" value={`${cot.mm_index_52w}/100`} testid="cot-idx" />
          <KV label="WoW MM Δ" value={`${cot.mm_wow_delta > 0 ? "+" : ""}${formatNum(cot.mm_wow_delta)}`}
              className={cot.mm_wow_delta > 0 ? "text-green-400" : "text-red-400"} testid="cot-wow" />
          <KV label="Open Interest" value={formatNum(cot.open_interest)} testid="cot-oi" />
          <KV label="OI Δ WoW" value={formatPct(cot.oi_wow_delta_pct)} testid="cot-oi-delta" />
          <KV label="Prod/Merch Net" value={formatNum(cot.pm_net)} className="text-red-300" testid="cot-pm" />
          <KV label="Swap Dealer Net" value={formatNum(cot.swap_net)} testid="cot-swap" />
          <KV label="MM % of OI" value={`${cot.mm_pct_of_oi}%`} testid="cot-pct-oi" />
        </div>
      </CardContent>
    </Card>
  );
};

const MacroCard = ({ macro }) => {
  if (!macro) return null;
  return (
    <Card data-testid="macro-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-400" /> Macro Context
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 text-sm">
          {macro.interpretation}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <KV label="10Y Real Yield" value={`${macro.real_yield_10y?.toFixed(2)}%`} testid="macro-ry" />
          <KV label="Real Yield 30d" value={formatBps(macro.real_yield_30d_change_bps)}
              className={macro.real_yield_30d_change_bps < 0 ? "text-green-400" : "text-red-400"} testid="macro-ry-30d" />
          <KV label="DXY" value={macro.dxy?.toFixed(2)} testid="macro-dxy" />
          <KV label="DXY 200DMA" value={macro.dxy_200dma?.toFixed(2)} testid="macro-dxy-dma" />
          <KV label="DXY 30d" value={formatPct(macro.dxy_30d_change_pct)} testid="macro-dxy-30d" />
          <KV label="VIX" value={`${macro.vix?.toFixed(1)} (${macro.vix_regime})`} testid="macro-vix" />
          <KV label="Gold/Silver" value={macro.gold_silver_ratio?.toFixed(1)} testid="macro-gsr" />
        </div>
      </CardContent>
    </Card>
  );
};

const AgentCard = ({ title, icon: Icon, color, text }) => (
  <Card data-testid={`agent-${title.toLowerCase().split(" ")[0]}`}>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} /> {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <MarkdownText>{text || "—"}</MarkdownText>
    </CardContent>
  </Card>
);

const KV = ({ label, value, className = "", testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`text-base font-mono font-semibold ${className}`}>{value}</div>
  </div>
);
